import React, { ReactNode } from "react";
import { ScrollViewNavigation, ScrollViewNavigationProps } from "../coobjects/ScrollViewNavigation";

/**
 * Optional thin progress-bar strip rendered flush against this panel's top edge, so it reads as
 * the panel's own top border. Purely presentational — FooterPanel never owns when to show/fade/
 * hide it: the caller recomputes `percent`/`fading` every render and simply stops passing
 * `progressBar` once the strip should be gone.
 */
export interface FooterProgressBarProps {
  /** Completion percentage in the [0, 100] range -- becomes the native `<progress>`'s `value`. */
  percent: number;
  /** True while the strip is animating out after reaching 100% (opacity fades to 0). Default: false. */
  fading?: boolean;
  /** Skip the width/opacity CSS transitions and snap instantly (prefers-reduced-motion). Default: false. */
  reducedMotion?: boolean;
}

export interface FooterPanelProps {
  children: ReactNode;
  isLocked?: boolean;
  scrollViewNavigationProps?: ScrollViewNavigationProps;
  /**
   * Optional progress-bar strip along this panel's top edge — see `FooterProgressBarProps`.
   * Default: undefined (off).
   */
  progressBar?: FooterProgressBarProps;
}

function FooterPanel(props: Readonly<FooterPanelProps>) {
  const { progressBar } = props;
  // `.rrp-progress-animated` is the ONLY thing this flag toggles -- see the CSS comment in
  // colors.css. Track/fill colors always apply; only the width/opacity transitions are opt-in.
  const animatedSuffix = progressBar && !progressBar.reducedMotion ? " rrp-progress-animated" : "";

  return (
    <div className="ms-Grid-row">
      <div
        className={"ms-Grid-col ms-sm12 ms-md12 ms-lg12"}
        style={{
          position: "fixed",
          zIndex: "99",
          bottom: 0,
          left: 0,
          backgroundColor: props.isLocked ? "var(--color-yellow)" : "var(--color-lightblue)",
          padding: "15px 15px",
        }}
      >
        {progressBar && (
          <progress
            data-testid="rrp-progress-track"
            className={`rrp-progress-track${animatedSuffix}`}
            value={progressBar.percent}
            max={100}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              width: "100%",
              height: 3,
              opacity: progressBar.fading ? 0 : 1,
            }}
          />
        )}
        {props.children}
        {props.scrollViewNavigationProps && (
          <div
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              top: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pointerEvents: "none",
            }}
          >
            <ScrollViewNavigation {...props.scrollViewNavigationProps} />
          </div>
        )}
      </div>
    </div>
  );
}

export default FooterPanel;
